<template lang="pug">
  .rogue-returns-system
    .main-container.rogue-rma-page
      h1.underline {{ __('Returns') }}
      NavigationTabs(:data="navigationTabsData")
      rma-skeleton(v-if="fetching")
      router-view(
        v-else
        :key="route.name"
        :order="order"
        :form-data="formData"
        :rma="route.name === 'rmaPrintReturn' && rma"
        :processing="fetching"
        :status="status"
        @reset-status="status = null"
        @order="fetchOrder"
        @success="rma = $event"
      )
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import NavigationTabs from './components/rma-navigation-tabs.vue'
import RmaSkeleton from './components/rma-skeleton.vue'
import { useRogue } from '@/js/services/Rogue'

interface FormData {
  email: string
  orderNumber: string
  name: string
  phone: string
  token: string | null
  items: any[]
  shippingCode?: string
}

const route = useRoute()
const router = useRouter()
const rogue = useRogue()

const fetching = ref<boolean>(true)
const status = ref<string | null>(null)
const order = ref<any>({})
const rma = ref<any>(null)

const formData = reactive<FormData>({
  email       : '',
  orderNumber : '',
  name        : '',
  phone       : '',
  token       : null,
  items       : [],
})

const navigationTabsData = computed(() => ({
  email       : formData.email,
  orderNumber : formData.orderNumber,
  name        : formData.name,
  phone       : formData.phone,
  returnItems : formData.items,
  rmaSuccess  : rma.value,
}))

watch(() => formData.shippingCode, (value) => {
  if (('shippingCode' in formData) && (value === null || value === undefined)) {
    delete formData.shippingCode
  }
})

watch(() => formData.orderNumber, (value) => {
  if (value) {
    formData.orderNumber = value.toUpperCase()
    // router.replace({ query: { ...route.query, order: value } })
  }
})

watch(status, (value) => {
  if (value === 'not_found' && route.path === '/rmaprocess') {
    router.replace({ path: '/rmaprocess/initiate', query: route.query })
  }
})

onMounted(() => load())

async function load() {
  if (route.query.token) {
    await fetchOrder()
  } else if (route.query.data && route.query.order) {
    formData.email = decodeURIComponent(atob(route.query.data as string))
    formData.orderNumber = route.query.order as string
    await fetchOrder()
  } else {
    await router.replace({ path: '/rmaprocess/initiate', query: route.query })
    fetching.value = false
  }
}

async function fetchOrder() {
  fetching.value = true
  try {
    const orderResponse = route.query.token
      ? await rogue.fetch<any>(`/svc_rma/order?token=${route.query.token}`)
      : await rogue.fetch<any>(`/svc_rma/order?email=${encodeURIComponent(formData.email)}&incrementId=${formData.orderNumber}`)

    if (orderResponse) {
      try {
        const tokenResp = await rogue.fetch<{ token: string }>(`/svc_checkout/order/token`, 'POST', {
          email   : orderResponse?.customer?.email,
          orderId : orderResponse?.incrementId,
        })
        route.query.token = encodeURIComponent(tokenResp.token)
      } catch (e) {
        rogue.log.warn('RMA: Unable to fetch order token:', e)
      }

      setOrder(orderResponse)
      await router.replace({ path: '/rmaprocess/select-items', query: route.query })
      fetching.value = false

      return
    }
  } catch (e: any) {
    rogue.log.error('RMA: Unable to fetch information about the order:', e)
    status.value = e.error
  }
  fetching.value = false
}

function setOrder(orderData: any) {
  order.value = orderData || {}
  formData.items = []
  formData.token = orderData.token || null
  formData.name = orderData?.customer?.name || ''
  formData.phone = orderData?.customer?.phone || ''
  formData.email = orderData?.customer?.email || ''
  formData.orderNumber = orderData?.incrementId || ''
}
</script>

<style lang="scss" scoped>
.rogue-returns-system{
  min-height: 60vh;
}

.rogue-rma-page {
  margin-top: $space-l;
}

h1.underline {
  padding-bottom: $space-s;
  margin-bottom: $space-m;
}

:deep() {
  section {
    margin-bottom: $space-m;

    &:not(.shipping-addresses) .column {
      padding-bottom: 0;
    }

    .title {
      color: $color-white;
      background-color: $color-black;
      text-transform: uppercase;
      font-family: $font-condensed;
      font-size: $size-s;
      padding: $space-s;
      line-height: $line-height-s;
    }
  }

  .required {
    color: $color-red;
  }

  .box {
    border: $border-width-thin solid $color-gray;
  }

  .product {
    padding: $space-s;
    border: $border-width-thin solid $color-gray;
    margin-top: -$border-width-thin;
  }

  .button.form-field {
    margin-bottom: $space-xxs;
  }

  select,
  input:not([type="radio"]),
  .button {
    max-height: 44px;
    min-height: 44px;
  }

  @include mobile {
    .form-field:not(.button):not(.field-phone):not(.field-email) {
      margin-bottom: $space-s;
    }
  }

  @include not-mobile {
    .button.form-field {
      margin-top: 18px;
    }

    .form-field {
      margin-bottom: $space-xxs;
    }
  }
}
</style>
